import axios from 'axios';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  DrawingShapeProcessingContext,
  ProductMappingContext,
  SideItemProductsConfig,
} from './pricedProductsContext.utils';
import {
  accumulateDuplicateCornerRadii,
  addSideItemProducts,
  fetchUnknownProductPrices,
  getNumberOfChipMinimizersInDrawingShapes,
  getPricedCookingAppliances,
  getPricedCooktopCutouts,
  getPricedCutouts,
  getPricedFaucets,
  getPricedPlumbing,
  getPricedSinksCutouts,
  getPricedTearOuts,
  mapCompletedProducts,
  mapSinkRecordToPricedSinks,
  processDrawingShapes,
  reduceDuplicateProducts,
} from './pricedProductsContext.utils';

import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import { useSelectCutoutLabor } from '~/api/easy-estimator-api/cutout-labor-api/hooks/useCutoutLabor';
import { useDiagonalCorners } from '~/api/easy-estimator-api/diagonal-corners-api/useDiagonalCorners';
import { useEndCaps } from '~/api/easy-estimator-api/endcaps-api/useEndCaps';
import { useEndSplash } from '~/api/easy-estimator-api/endsplash-api/useEndSplash';
import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { useLazyFetchProductsPricesQuery } from '~/api/easy-estimator-api/priced-products-api/pricedProductsService';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { useWaterfallLabor } from '~/api/easy-estimator-api/waterfall-labor-api/useWaterfallLabor';
import { ProductType, productTypeSortOrder } from '~/core/enums';
import { isInsideDiagonalCornerProduct } from '~/core/helpers';
import { useShapeArea } from '~/core/hooks/useShapeArea';
import { useSink } from '~/core/hooks/useSink';
import { useSmallJobs } from '~/core/hooks/useSmallJobs';
import { PricedProduct, ProductEstimateQuery, ProductQty } from '~/core/models';
import {
  createTypeSafeContext,
  generateCanvasImage,
  isSomething,
} from '~/core/utils';
import {
  PriceTotalsReturn,
  calculateTotalPrice,
} from '~/core/utils/price.utils';
import {
  ProductFilters,
  doesProductMatchFilter,
} from '~/core/utils/productFilter.utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import {
  selectBrowsedCookingAppliances,
  selectSelectedAppliance,
  selectSuppliedCookingAppliances,
} from '~/redux/slices/cookingAppliance';
import { selectCustomCutouts } from '~/redux/slices/cutout';
import {
  selectPreventRepricing,
  setPreventRepricing,
} from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { selectSelectedMaterial } from '~/redux/slices/material';
import { selectSelectedPlumbing } from '~/redux/slices/plumbing';
import {
  resetAllPricing as resetAllPricingAction,
  setCanvasJpeg,
} from '~/redux/slices/pricedProducts';
import {
  buildEdgesPrice,
  getAggregatedPricedProducts,
  getPricingEstimate,
} from '~/redux/slices/pricedProducts/pricedProducts.utils';
import {
  selectNumberOfSelectedChipMinimizers,
  selectSelectedSinks,
} from '~/redux/slices/sink';
import { selectSelectedTearOuts } from '~/redux/slices/tearOut';
import { selectZipcode } from '~/redux/slices/zipcode';
import {
  useDrawingShapeContext,
  useFaucetContext,
  useOptionalProductContext,
  useProductDetailsContext,
} from '~contexts';

export interface PricedProductsContextValue {
  pricedProducts: PricedProduct[];
  resetPricedProducts: () => void;
  resetAllPricing: () => void;

  priceTotalsWithoutDelivery: PriceTotalsReturn;
  priceTotalsWithDelivery: PriceTotalsReturn;

  ///// usePricedProducts legacy /////
  error: boolean;
  ///// END usePricedProducts legacy /////
  pricedProductsWithoutDelivery: PricedProduct[];
}

export const [usePricedProductsContext, PricedProductsContext] =
  createTypeSafeContext<PricedProductsContextValue>();

const emptyArray = [] satisfies PricedProduct[];

export const PricedProductsContextProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);

  const [pricedProducts, setPricedProducts] =
    useState<PricedProduct[]>(emptyArray);

  const {
    isSmallJobWithFee,
    useMinQtyAreaForPricing,
    smallJobFeeProduct,
    isSmallJob,
    smallJobMinimumArea,
  } = useSmallJobs();

  const { totalBaseCountertopArea: countertopAreaQty } = useShapeArea();

  const selectedCooktops = useAppSelector(
    selectSelectedAppliance('selectedCooktopApplianceProduct')
  );
  const selectedRanges = useAppSelector(
    selectSelectedAppliance('selectedRangeApplianceProduct')
  );
  const browsedCookingAppliances = useAppSelector(
    selectBrowsedCookingAppliances
  );
  const suppliedCookingAppliances = useAppSelector(
    selectSuppliedCookingAppliances
  );

  const resetPricedProducts = () => {
    setPricedProducts(emptyArray);
  };

  const resetAllPricing = () => {
    dispatch(resetAllPricingAction());
    resetPricedProducts();
  };

  ///// usePricedProducts legacy /////

  const zipCode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();
  const selectedColor = useAppSelector(selectSelectedColor);
  const selectedMaterial = useAppSelector(selectSelectedMaterial);
  const preventRepricing = useAppSelector(selectPreventRepricing);

  // TODO DrawingShapeContext should not be returning "priced" items
  const {
    getters: { drawingShapes, selectedSinksInDrawing: pricedSinksInDrawing },
  } = useDrawingShapeContext();
  const { productCatalog } = useProductDetailsContext();
  const { selectSinkCutout, cooktopCutout } = useSelectCutoutLabor();
  const { getOptionalProductsProductQty } = useOptionalProductContext();

  const [error, setError] = useState(false);

  // Only sinks not in the drawing area
  const selectedExtraSinks = useAppSelector(selectSelectedSinks);

  /** Number of chip minimizers in the drawing panel ONLY. Chip minimizers in the drawing itself are not included in this count */
  const numberOfSelectedChipMinimizer = useAppSelector(
    selectNumberOfSelectedChipMinimizers
  );

  const selectedTearOuts = useAppSelector(selectSelectedTearOuts);
  const selectedCutouts = useAppSelector(selectCustomCutouts).selected;
  const { cornerRadii } = useCornerRadii();
  const { diagonalCorners } = useDiagonalCorners();

  const { waterfallLaborFee } = useWaterfallLabor();
  const { endCaps } = useEndCaps();
  const { endSplashes } = useEndSplash();
  const { selectedFaucets } = useFaucetContext();
  const selectedPlumbing = useAppSelector(selectSelectedPlumbing);

  const { chipMinimizer } = useOptionalItems();

  const {
    selectedMaterialAreaType,
    totalBacksplashArea,
    totalWaterfallArea,
    totalWaterfallLaborEdgeLengths,
    totalEndCapLaborEdgeCount,
    totalEndSplashLaborEdgeCount,
  } = useShapeArea();

  const oversizeFeeProductForIslands = useMemo(() => {
    const oversizeFeeProducts = Object.values(productCatalog).filter(
      (product) =>
        doesProductMatchFilter(ProductFilters.oversizeFeeForIslands, product)
    );

    return oversizeFeeProducts.length === 1
      ? oversizeFeeProducts[0]
      : undefined;
  }, [productCatalog]);

  // Match EndCap to selected color group via qteSubCode
  const matchingEndCap = useMemo(
    () =>
      endCaps?.find(
        (endCap) => endCap.qteSubCode === selectedColor?.colorGroup
      ),
    [endCaps, selectedColor?.colorGroup]
  );

  // Match EndSplash to selected color group via qteSubCode
  const matchingEndSplash = useMemo(
    () =>
      endSplashes?.find(
        (endSplash) => endSplash.qteSubCode === selectedColor?.colorGroup
      ),
    [endSplashes, selectedColor?.colorGroup]
  );

  const [fetchLazyProductsPrices] = useLazyFetchProductsPricesQuery();

  const { browsedSinks, customerSuppliedSinks } = useSink();

  const getProducts = useCallback(() => {
    if (!selectedColor || !countertopAreaQty) {
      return [];
    }

    const pricedColor: ProductQty = {
      productId: selectedColor.productId,
      quantity: countertopAreaQty,
    };
    const pricedEdges = buildEdgesPrice(drawingShapes);
    const pricedTearOuts = getPricedTearOuts(selectedTearOuts);
    const pricedOptionalProducts = getOptionalProductsProductQty();

    const pricedCutouts = getPricedCutouts(selectedCutouts);
    const pricedFaucets = getPricedFaucets(selectedFaucets);
    const pricedPlumbing = getPricedPlumbing(selectedPlumbing);

    const allCookingAppliances = [
      ...browsedCookingAppliances,
      ...suppliedCookingAppliances,
    ];

    /**
     * all cooktops and ranges that are _actual products_, not "customner supplied"
     */
    const pricedCooktops = getPricedCookingAppliances(
      selectedCooktops.selected,
      selectedRanges.selected,
      allCookingAppliances
    );

    /**
     * All cutouts for all cooktops (not ranges), including actual products and "customer supplied"
     */
    const pricedCooktopCutouts = getPricedCooktopCutouts(
      selectedCooktops.selected,
      cooktopCutout
    );

    /*
     * Sinks not in the drawing are those added in side panel using the counters instead of dragging them to the drawing
     */
    const pricedSinksNotInDrawing =
      mapSinkRecordToPricedSinks(selectedExtraSinks);

    const pricedSinks = [...pricedSinksInDrawing, ...pricedSinksNotInDrawing];

    const numberOfChipMinimizersInDrawingShapes =
      getNumberOfChipMinimizersInDrawingShapes(drawingShapes);

    const pricedChipMinimizersInDrawing = [
      {
        productId: chipMinimizer?.productId,
        quantity: numberOfChipMinimizersInDrawingShapes,
      },
    ].filter((item) => item.productId) as ProductQty[];

    const pricedChipMinimizersNotInDrawing = [
      {
        productId: chipMinimizer?.productId,
        quantity: numberOfSelectedChipMinimizer,
      },
    ].filter((item) => item.productId) as ProductQty[];

    const pricedChipMinimizers = [
      ...pricedChipMinimizersInDrawing,
      ...pricedChipMinimizersNotInDrawing,
    ];

    const sinksProducts = [browsedSinks, customerSuppliedSinks].flat();
    const pricedSinksCutouts = getPricedSinksCutouts(
      pricedSinks,
      sinksProducts,
      selectSinkCutout,
      selectedMaterial?.productLine
    );

    return [
      pricedColor,
      pricedEdges,
      pricedSinks,
      pricedSinksCutouts,
      pricedTearOuts,
      pricedOptionalProducts,
      pricedCutouts,
      pricedChipMinimizers,
      pricedFaucets,
      pricedPlumbing,
      pricedCooktops,
      pricedCooktopCutouts,
    ]
      .flat()
      .filter(isSomething);
  }, [
    selectedColor,
    countertopAreaQty,
    drawingShapes,
    selectedTearOuts,
    getOptionalProductsProductQty,
    selectedCutouts,
    selectedFaucets,
    selectedPlumbing,
    browsedCookingAppliances,
    suppliedCookingAppliances,
    selectedCooktops.selected,
    selectedRanges.selected,
    cooktopCutout,
    selectedExtraSinks,
    pricedSinksInDrawing,
    chipMinimizer?.productId,
    numberOfSelectedChipMinimizer,
    browsedSinks,
    customerSuppliedSinks,
    selectSinkCutout,
    selectedMaterial?.productLine,
  ]);

  const addProductEstimateQuery = useCallback(async () => {
    // ============================================================================
    // VALIDATION
    // ============================================================================
    if (
      !selectedStore ||
      !selectedMaterial ||
      !selectedColor ||
      !selectedColor?.productId
    ) {
      return [];
    }

    // ============================================================================
    // BUILD INITIAL PRODUCT ESTIMATE QUERY
    // ============================================================================
    const productEstimateQuery: ProductEstimateQuery = {
      storeId: selectedStore.companyId,
      zipCode,
      products: getProducts(),
    };

    // ============================================================================
    // PROCESS DRAWING SHAPES
    // ============================================================================
    const insideDiagonalCorner = diagonalCorners.find(
      isInsideDiagonalCornerProduct
    );

    const drawingShapeContext: DrawingShapeProcessingContext = {
      insideDiagonalCorner,
      cornerRadii,
      oversizeFeeProduct: oversizeFeeProductForIslands,
      selectedMaterialAreaType,
    };

    processDrawingShapes(
      drawingShapes,
      drawingShapeContext,
      productEstimateQuery
    );

    // ============================================================================
    // ADD SIDE ITEM PRODUCTS
    // ============================================================================
    const sideItemConfig: SideItemProductsConfig = {
      totalBacksplashArea,
      totalWaterfallArea,
      totalWaterfallLaborEdgeLengths,
      waterfallLaborFee,
      totalEndCapLaborEdgeCount,
      matchingEndCap,
      totalEndSplashLaborEdgeCount,
      matchingEndSplash,
      isSmallJobWithFee,
      smallJobFeeProduct,
      selectedColorProductId: selectedColor.productId,
    };

    addSideItemProducts(sideItemConfig, productEstimateQuery);

    // ============================================================================
    // DEDUPLICATE PRODUCTS
    // ============================================================================
    productEstimateQuery.products = accumulateDuplicateCornerRadii(
      productEstimateQuery.products
    );
    productEstimateQuery.products = reduceDuplicateProducts(
      productEstimateQuery.products
    );

    // ============================================================================
    // FETCH PRICES AND MAP PRODUCTS
    // ============================================================================
    const [unknownProductsQuery, completedPricedProducts] = getPricingEstimate({
      productCatalog,
      productEstimateQuery,
      selectedColor,
    });

    const allPricedProducts = await fetchUnknownProductPrices(
      unknownProductsQuery,
      completedPricedProducts,
      fetchLazyProductsPrices
    );

    const mappingContext: ProductMappingContext = {
      selectedColor,
      backsplashTitle: t('BACKSPLASH.TITLE'),
      waterfallTitle: t('WATERFALL.TITLE'),
    };

    return mapCompletedProducts(allPricedProducts, mappingContext);
  }, [
    selectedStore,
    selectedMaterial,
    selectedColor,
    zipCode,
    getProducts,
    diagonalCorners,
    drawingShapes,
    totalBacksplashArea,
    totalWaterfallArea,
    totalWaterfallLaborEdgeLengths,
    waterfallLaborFee,
    totalEndCapLaborEdgeCount,
    matchingEndCap,
    totalEndSplashLaborEdgeCount,
    matchingEndSplash,
    productCatalog,
    oversizeFeeProductForIslands,
    selectedMaterialAreaType,
    cornerRadii,
    fetchLazyProductsPrices,
    t,
    isSmallJobWithFee,
    smallJobFeeProduct,
  ]);

  const mapPricedProductTypes = (product: PricedProduct): PricedProduct => {
    const fixedProdTypeByConsumerDesc: [string, ProductType][] = [
      ['mileage', ProductType.MILEAGE_TIER],
      ['faucet', ProductType.FAUCET_CUTOUT],
      ['electric', ProductType.ELECTRICAL_CUTOUT],
    ];

    return {
      ...product,
      productType:
        fixedProdTypeByConsumerDesc.find(([matchingDesc]) =>
          product.consumerDesc?.toLowerCase().includes(matchingDesc)
        )?.[1] ?? product.productType,
    };
  };

  const _getAggregatedPricedProducts = useCallback(async () => {
    const pricedProductsFromEstimateQuery = await addProductEstimateQuery();
    const aggregatedPricedProducts = getAggregatedPricedProducts({
      drawingShapes,
      pricedProducts: pricedProductsFromEstimateQuery,
      selectedColor,
      selectedMaterial,
    }).map(mapPricedProductTypes);

    return aggregatedPricedProducts.toSorted(
      (a, b) =>
        productTypeSortOrder[a.productType] -
        productTypeSortOrder[b.productType]
    );
  }, [addProductEstimateQuery, drawingShapes, selectedColor, selectedMaterial]);

  const getPricedProducts = useCallback(async () => {
    try {
      if (countertopAreaQty) {
        setError(false);

        const aggregatedPricedProducts = await _getAggregatedPricedProducts();

        setPricedProducts(aggregatedPricedProducts);
      } else if (pricedProducts.length) {
        resetPricedProducts();
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return [];
      }
      console.error(error);

      setError(true);
    }

    return [];
  }, [_getAggregatedPricedProducts, countertopAreaQty, pricedProducts.length]);

  /**
   * Priced products with delivery products removed (i.e. any mileage tier)
   * used on summary page
   */
  const pricedProductsWithoutDelivery = useMemo(
    () =>
      pricedProducts.filter(
        (pp) => pp.productType !== ProductType.MILEAGE_TIER
      ),
    [pricedProducts]
  );

  /**
   * price totals for the product excluding delivery
   * used on summary page
   */
  const priceTotalsWithoutDelivery = useMemo(
    () =>
      calculateTotalPrice({
        pricedProducts: pricedProductsWithoutDelivery,
        selectedColor,
        isSmallJob,
        useMinQtyAreaForPricing,
        smallJobMinimumArea,
      }),
    [
      pricedProductsWithoutDelivery,
      selectedColor,
      isSmallJob,
      useMinQtyAreaForPricing,
      smallJobMinimumArea,
    ]
  );

  /**
   * price totals for the product excluding delivery
   * used on summary page
   */
  const priceTotalsWithDelivery = useMemo(
    () =>
      calculateTotalPrice({
        pricedProducts,
        selectedColor,
        isSmallJob,
        useMinQtyAreaForPricing,
        smallJobMinimumArea,
      }),
    [
      pricedProducts,
      selectedColor,
      isSmallJob,
      useMinQtyAreaForPricing,
      smallJobMinimumArea,
    ]
  );

  /*
   * Updates the canvas image whenever the product's estimate query changes.
   */
  const updateCanvasOrderSummaryImage = useCallback(
    async (canvas: fabric.Canvas) => {
      const pricedProductsFromEstimateQuery = await addProductEstimateQuery();

      if (pricedProductsFromEstimateQuery.length) {
        const jpeg = generateCanvasImage('jpeg', canvas);

        setCanvasJpeg(jpeg);
      }
    },
    [addProductEstimateQuery]
  );

  useEffect(() => {
    if (preventRepricing) {
      dispatch(setPreventRepricing(false));

      return;
    }

    updateCanvasOrderSummaryImage(canvas);

    getPricedProducts();
  }, [
    dispatch,
    getPricedProducts,
    preventRepricing,
    updateCanvasOrderSummaryImage,
    canvas,
  ]);

  ///// END usePricedProducts legacy /////

  const pricedProductsValue: PricedProductsContextValue = {
    pricedProducts,
    resetPricedProducts,
    resetAllPricing,
    ///// usePricedProducts legacy /////
    error,
    ///// END usePricedProducts legacy /////
    pricedProductsWithoutDelivery,
    priceTotalsWithoutDelivery,
    priceTotalsWithDelivery,
  };

  return (
    <PricedProductsContext.Provider value={pricedProductsValue}>
      {children}
    </PricedProductsContext.Provider>
  );
};
